import React from "react";
import { Tag, Alert, Typography, Space } from "antd";
import {
  AppstoreOutlined,
  CarOutlined,
  OrderedListOutlined,
  ClusterOutlined,
  FileProtectOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  AccountBookOutlined,
  PercentageOutlined,
  TruckOutlined,
  TeamOutlined,
  DashboardOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";

const { Text, Paragraph, Title } = Typography;

export type HelpContent = {
  title: string;
  icon: React.ReactNode;
  tagline: string;
  sections: HelpSection[];
};

type HelpSection = {
  heading: string;
  content: React.ReactNode;
};

const Step = ({ n, text }: { n: number; text: string }) => (
  <div style={{ display: "flex", gap: 10, marginBottom: 6, alignItems: "flex-start" }}>
    <div style={{
      minWidth: 22, height: 22, borderRadius: "50%", background: "var(--primary, #1677ff)",
      color: "#fff", fontSize: 11, fontWeight: 700, display: "flex",
      alignItems: "center", justifyContent: "center", marginTop: 1,
    }}>{n}</div>
    <Text>{text}</Text>
  </div>
);

const Tip = ({ children }: { children: React.ReactNode }) => (
  <Alert
    type="info"
    showIcon
    message={children}
    style={{ fontSize: 12, marginTop: 8, padding: "6px 10px" }}
  />
);

const Warn = ({ children }: { children: React.ReactNode }) => (
  <Alert
    type="warning"
    showIcon
    message={children}
    style={{ fontSize: 12, marginTop: 8, padding: "6px 10px" }}
  />
);

// ── FULL SYSTEM FLOW ───────────────────────────────────────────────────────────
export const FULL_FLOW_HELP: HelpContent = {
  title: "Transport ERP — Full Workflow",
  icon: <TruckOutlined />,
  tagline: "End-to-end goods movement from booking to delivery",
  sections: [
    {
      heading: "Phase 1 — One-Time Setup (Master Data)",
      content: (
        <Space direction="vertical" style={{ width: "100%" }} size={4}>
          <Step n={1} text="Create a Financial Year (e.g. 2026-27) and mark it Active." />
          <Step n={2} text="Register your Branches (booking offices, hubs, delivery points)." />
          <Step n={3} text="Configure Document Sequences — set numbering prefix and branch for Consignment, Manifest, and GatePass for each Financial Year." />
          <Step n={4} text="Add Parties — register consignors (senders), consignees (receivers), and billing customers." />
          <Step n={5} text="Register your Fleet (vehicles) and Drivers." />
          <Step n={6} text="Configure Tax Profiles (GST SAC codes and rates) if accounting integration is needed." />
          <Tip>Document sequences must be re-configured when a new Financial Year is activated. Sequences are FY-scoped and do not carry forward automatically.</Tip>
        </Space>
      )
    },
    {
      heading: "Phase 2 — Daily Operations",
      content: (
        <Space direction="vertical" style={{ width: "100%" }} size={4}>
          <Step n={1} text="Consignment Booking → Create a Lorry Receipt (LR) for every consignment (goods shipment). Assign consignor, consignee, origin, destination, weight, and freight." />
          <Step n={2} text="E-Way Bill → For goods above ₹50,000 or inter-state movement, record the E-Way Bill number against the consignment." />
          <Step n={3} text="Lorry Manifest → Create a manifest (challan) for the vehicle, then Add Consignment to load booked LRs onto it. Dispatch when loaded." />
          <Step n={4} text="Trip Planning → Create a trip linked to the manifest vehicle and route for tracking fuel, expenses, and odometer." />
          <Step n={5} text="Delivery → Record delivery confirmation once consignments reach destination." />
          <Tip>Consignment status follows: Booked → Loaded (when added to manifest) → Delivered (on delivery) → Cancelled.</Tip>
        </Space>
      )
    },
    {
      heading: "Phase 3 — Finance",
      content: (
        <Space direction="vertical" style={{ width: "100%" }} size={4}>
          <Step n={1} text="Lorry Hire → If you use external vehicles, raise a Lorry Hire Challan for the vehicle owner and settle payment." />
          <Step n={2} text="Commission → Configure rules for agents/brokers, generate monthly commission periods, approve statements, and record payments." />
          <Step n={3} text="Finance & Accounting → Review journal entries, ledgers, and postings generated by operations." />
        </Space>
      )
    },
    {
      heading: "Phase 4 — Reporting",
      content: (
        <Space direction="vertical" style={{ width: "100%" }} size={4}>
          <Step n={1} text="Booking Report → Monthly consignment register filtered by destination gate or branch. Shows To-Pay, Paid, and TBB totals." />
          <Step n={2} text="Manifest Report → Per-manifest challan printout listing all loaded consignments." />
          <Tip>Use the Print button on the Manifest detail drawer to get a print-ready challan for the driver.</Tip>
        </Space>
      )
    }
  ]
};

// ── PER-MODULE HELP ────────────────────────────────────────────────────────────

export const MODULE_HELP: Record<string, HelpContent> = {

  "/": {
    title: "Dashboard",
    icon: <DashboardOutlined />,
    tagline: "At-a-glance summary of today's operations",
    sections: [
      {
        heading: "What you see here",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Paragraph style={{ margin: 0 }}>
              Live counters for active consignments, running trips, registered vehicles, and uploaded documents. All figures are scoped to the active financial year and your organization.
            </Paragraph>
            <Tip>If all counts show 0, check that a Financial Year is active in Master Data.</Tip>
          </Space>
        )
      },
      {
        heading: "Quick start",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Go to Master Data → Financial Years and activate a year." />
            <Step n={2} text="Add document sequences for Consignment and Manifest." />
            <Step n={3} text="Register at least one Branch, Vehicle, and Party." />
            <Step n={4} text="Start booking consignments." />
          </Space>
        )
      }
    ]
  },

  "/consignments": {
    title: "Consignment Booking",
    icon: <AppstoreOutlined />,
    tagline: "Create and manage Lorry Receipts (LRs)",
    sections: [
      {
        heading: "What is a Consignment?",
        content: (
          <Paragraph style={{ margin: 0 }}>
            A Consignment (also called an LR — Lorry Receipt) is the primary transport document. It records a single shipment of goods from a consignor (sender) to a consignee (receiver), including weight, packages, freight amount, and payment type.
          </Paragraph>
        )
      },
      {
        heading: "How to create a consignment",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Click New Consignment." />
            <Step n={2} text="Select Billing Party (who pays the freight bill)." />
            <Step n={3} text="Select Consignor (the sender) and Consignee (the receiver)." />
            <Step n={4} text="Set Booking Branch (your office) and Delivery Branch (destination office)." />
            <Step n={5} text="Enter weight, packages, goods name, and freight amount." />
            <Step n={6} text="Choose Payment Type: To Pay (receiver pays on delivery), Paid (sender paid upfront), To Be Billed (monthly billing), or FOC (free)." />
            <Step n={7} text="Click Save. The system assigns the next LR number from the document sequence." />
            <Warn>If you get 'Document sequence not configured', go to Master Data → Document Sequences and add a Consignment sequence for the active financial year and your booking branch (or use MAIN as a catch-all).</Warn>
          </Space>
        )
      },
      {
        heading: "After booking",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Paragraph style={{ margin: 0 }}>The consignment status starts as <Tag color="blue">Booked</Tag>. It becomes <Tag color="orange">Loaded</Tag> when added to a manifest, and <Tag color="green">Delivered</Tag> on delivery confirmation.</Paragraph>
            <Tip>To attach an E-Way Bill to a consignment, click the E-Way button on the consignment row.</Tip>
          </Space>
        )
      }
    ]
  },

  "/manifests": {
    title: "Lorry Manifest",
    icon: <OrderedListOutlined />,
    tagline: "Create vehicle challans and load consignments",
    sections: [
      {
        heading: "What is a Manifest?",
        content: (
          <Paragraph style={{ margin: 0 }}>
            A Manifest (also called a Challan or Loading Sheet) records all consignments loaded onto a specific vehicle for a trip. It is the vehicle's authorization to carry those goods. The driver carries a printed copy.
          </Paragraph>
        )
      },
      {
        heading: "Workflow",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Click New Manifest. Select the vehicle, driver, dispatch date, From Branch, and To Branch." />
            <Step n={2} text="Save — a manifest number (e.g. MNF000001) is generated." />
            <Step n={3} text="Click the manifest row to open its Detail View." />
            <Step n={4} text="Click Add Consignment — a drawer opens showing all Booked consignments." />
            <Step n={5} text="Click Add next to each consignment you want to load onto this vehicle. Their status changes to Loaded." />
            <Step n={6} text="Use Print to generate the challan for the driver." />
            <Tip>A consignment must be in Booked status to be added to a manifest. Already-Loaded or Delivered consignments will not appear in the search.</Tip>
            <Warn>Document sequence 'Manifest' must be configured for the From Branch (or MAIN) in the active financial year. Otherwise manifest creation will fail.</Warn>
          </Space>
        )
      }
    ]
  },

  "/trips": {
    title: "Trip Planning",
    icon: <ClusterOutlined />,
    tagline: "Track vehicle trips, fuel, and expenses",
    sections: [
      {
        heading: "What is a Trip?",
        content: (
          <Paragraph style={{ margin: 0 }}>
            A Trip tracks a vehicle's journey from origin to destination — recording fuel consumed, trip expenses, toll costs, odometer readings, and driver assignment. Trips are linked to manifests.
          </Paragraph>
        )
      },
      {
        heading: "How to use",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Create a trip after dispatching the manifest — assign the vehicle and route." />
            <Step n={2} text="Log fuel fills and trip expenses (toll, repair, loading charges) against the trip." />
            <Step n={3} text="Mark the trip Complete once the vehicle returns. Enter the return odometer." />
            <Tip>Trip expenses feed into cost-per-km reporting and profitability analysis.</Tip>
          </Space>
        )
      }
    ]
  },

  "/ewaybill": {
    title: "E-Way Bill",
    icon: <FileProtectOutlined />,
    tagline: "Track GST e-way bills for all consignments",
    sections: [
      {
        heading: "What is an E-Way Bill?",
        content: (
          <Paragraph style={{ margin: 0 }}>
            Under GST rules, goods worth ₹50,000+ or moving inter-state require an E-Way Bill (EWB) generated on the government portal. This module lets you record EWB numbers, track validity, and get alerts when they are about to expire.
          </Paragraph>
        )
      },
      {
        heading: "How to attach an E-Way Bill",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Generate the EWB externally on the GST portal (ewaybillgst.gov.in)." />
            <Step n={2} text="In Consignment Booking, click the E-Way button on the consignment row." />
            <Step n={3} text="Enter the 12-digit EWB number and validity date." />
            <Step n={4} text="The dashboard here shows active, expiring, and expired bills." />
            <Warn>You can enter the EWB number when creating a consignment (E-Way Bill No field) for convenience — this stores it on the LR without full lifecycle tracking.</Warn>
          </Space>
        )
      }
    ]
  },

  "/fleet": {
    title: "Fleet Registry",
    icon: <CarOutlined />,
    tagline: "Register and manage your vehicle fleet",
    sections: [
      {
        heading: "What goes here",
        content: (
          <Paragraph style={{ margin: 0 }}>
            All vehicles (trucks, trailers, tempos) owned or regularly used by your organization. Each vehicle is identified by its registration number (e.g. MH-04-AB-1234) and has capacity, type, and ownership details.
          </Paragraph>
        )
      },
      {
        heading: "Key points",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Add each vehicle before creating manifests — manifests require a vehicle selection." />
            <Step n={2} text="Log fuel fills and maintenance records against the vehicle for TCO tracking." />
            <Tip>For vehicles you hire (not own), register them here and create a Lorry Hire challan under Fleet → Lorry Hire.</Tip>
          </Space>
        )
      }
    ]
  },

  "/drivers": {
    title: "Driver Registry",
    icon: <TeamOutlined />,
    tagline: "Register drivers and their licence details",
    sections: [
      {
        heading: "What goes here",
        content: (
          <Paragraph style={{ margin: 0 }}>
            All drivers employed by your organization or regularly contracted. Driver name and licence number are the key fields. Drivers are assigned to manifests and trips.
          </Paragraph>
        )
      }
    ]
  },

  "/lorry-hire": {
    title: "Lorry Hire",
    icon: <TruckOutlined />,
    tagline: "Manage hired vehicle payments and challans",
    sections: [
      {
        heading: "What is Lorry Hire?",
        content: (
          <Paragraph style={{ margin: 0 }}>
            When you use an external/market vehicle (not owned by your company), you raise a Lorry Hire Challan for the vehicle owner specifying the freight rate, route, and payment terms.
          </Paragraph>
        )
      },
      {
        heading: "Workflow",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Create a Lorry Hire Challan — enter vehicle, owner, route, and agreed rate." />
            <Step n={2} text="Confirm the challan once the trip departs." />
            <Step n={3} text="Settle payment once the trip completes — enter payment mode and reference." />
            <Tip>Lorry Hire challans integrate with accounting to record the hire payable in the ledger.</Tip>
          </Space>
        )
      }
    ]
  },

  "/operations": {
    title: "Operations Utilities",
    icon: <ClusterOutlined />,
    tagline: "Quick overview of all live operational data",
    sections: [
      {
        heading: "What you see here",
        content: (
          <Paragraph style={{ margin: 0 }}>
            A tabbed view of all operational records — manifests, consignments, trips, fuel logs, maintenances, and trip expenses — with sorting and filtering. Use this as a quick lookup without navigating to individual modules.
          </Paragraph>
        )
      }
    ]
  },

  "/reports": {
    title: "Reports",
    icon: <BarChartOutlined />,
    tagline: "Booking register, manifest challan, and more",
    sections: [
      {
        heading: "Booking Report",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Paragraph style={{ margin: 0 }}>
              The monthly booking register lists all consignments for a selected month and year. Filter by destination gate or booking branch. Shows To-Pay, Paid, TBB totals and total freight.
            </Paragraph>
            <Step n={1} text="Select Year and Month." />
            <Step n={2} text="Optionally filter by Destination or Branch." />
            <Step n={3} text="Use Print to export a printable register." />
          </Space>
        )
      },
      {
        heading: "Manifest Report",
        content: (
          <Paragraph style={{ margin: 0 }}>
            Opens a per-manifest challan report. Accessed via the Print button inside the Manifest Detail drawer, or by entering a manifest number directly.
          </Paragraph>
        )
      }
    ]
  },

  "/accounting": {
    title: "Finance & Accounting",
    icon: <AccountBookOutlined />,
    tagline: "Ledgers, journal entries, and financial postings",
    sections: [
      {
        heading: "What is here",
        content: (
          <Paragraph style={{ margin: 0 }}>
            The accounting engine captures journal entries generated by transport operations — freight income, tax liabilities, hire payable, and commission payable. Review postings, check ledger balances, and configure posting profiles.
          </Paragraph>
        )
      },
      {
        heading: "Setup required",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Configure Accounting Settings — set your control accounts." />
            <Step n={2} text="Set up Posting Profiles linking transport events (freight collected, hire paid) to ledger accounts." />
            <Tip>Accounting postings are auto-generated when consignments and lorry hire challans are created/settled.</Tip>
          </Space>
        )
      }
    ]
  },

  "/commission": {
    title: "Commission",
    icon: <PercentageOutlined />,
    tagline: "Agent and broker commission management",
    sections: [
      {
        heading: "Workflow overview",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Add Beneficiaries — agents, brokers, or salespeople who earn commission." />
            <Step n={2} text="Create Commission Rules — define how much each beneficiary earns (% of freight, fixed per consignment, etc.) and for which routes/customers." />
            <Step n={3} text="Open a Commission Period (month + year) and Generate — the system calculates statements for each beneficiary." />
            <Step n={4} text="Review and Approve statements." />
            <Step n={5} text="Record Payment once settled." />
            <Tip>Commission is calculated on consignments that fall within the period's date range and match the rule criteria.</Tip>
          </Space>
        )
      }
    ]
  },

  "/parties": {
    title: "Party Registry",
    icon: <TeamOutlined />,
    tagline: "Customers, consignors, consignees, and vendors",
    sections: [
      {
        heading: "What is a Party?",
        content: (
          <Paragraph style={{ margin: 0 }}>
            A Party is any external entity your business interacts with — a customer (billing party), a consignor (goods sender), a consignee (goods receiver), or a vendor. The same entity can play multiple roles.
          </Paragraph>
        )
      },
      {
        heading: "Key fields",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Step n={1} text="Name — as it will appear on the LR and invoices." />
            <Step n={2} text="Code — short unique code used in document sequences and lookups." />
            <Step n={3} text="Type — Customer, Vendor, or Person." />
            <Tip>You can create parties on-the-fly from the Consignment Booking form — click Add New in any party dropdown.</Tip>
          </Space>
        )
      }
    ]
  },

  "/master-data": {
    title: "Master Data",
    icon: <DatabaseOutlined />,
    tagline: "Financial Years, Branches, Document Sequences, Tax Profiles",
    sections: [
      {
        heading: "Financial Years",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Paragraph style={{ margin: 0 }}>Defines the accounting year boundary (e.g. 01-Apr-2026 to 31-Mar-2027). Only one year can be Active at a time. All operational data is scoped to the active year.</Paragraph>
            <Warn>When you activate a new financial year, create new Document Sequences for it — they do not carry forward from the previous year.</Warn>
          </Space>
        )
      },
      {
        heading: "Branches",
        content: (
          <Paragraph style={{ margin: 0 }}>
            Physical offices, booking counters, or hub locations. Each branch has a short BranchCode (e.g. DEL, MUM, BLR) used in document sequences and consignment routing.
          </Paragraph>
        )
      },
      {
        heading: "Document Sequences",
        content: (
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Paragraph style={{ margin: 0 }}>
              Controls how document numbers are generated. Each sequence is for a specific Document Type (Consignment / Manifest / GatePass), Financial Year, and Branch.
            </Paragraph>
            <Step n={1} text="Use MAIN as the branch to apply a sequence as a fallback for all branches." />
            <Step n={2} text="Prefix + Padding gives you e.g. CSG000001 (prefix=CSG, padding=6)." />
            <Step n={3} text="You can have branch-specific sequences (DEL branch gets DEL000001) alongside a MAIN fallback." />
            <Warn>Missing document sequence = consignment/manifest creation will fail. Configure them immediately after activating a new financial year.</Warn>
          </Space>
        )
      },
      {
        heading: "Tax Profiles",
        content: (
          <Paragraph style={{ margin: 0 }}>
            Configure GST SAC codes and CGST/SGST/IGST rates for transport services. Required for the accounting engine to generate correct tax journal entries.
          </Paragraph>
        )
      }
    ]
  },

  "/attachments": {
    title: "Documents",
    icon: <FileSearchOutlined />,
    tagline: "Upload and manage transport documents",
    sections: [
      {
        heading: "What goes here",
        content: (
          <Paragraph style={{ margin: 0 }}>
            Upload scanned copies of LRs, pod (proof of delivery), e-way bills, vehicle fitness certificates, insurance documents, and driver licences. Documents are linked to the relevant entity (consignment, vehicle, driver).
          </Paragraph>
        )
      }
    ]
  }
};
